import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { getApiUrl } from '../utils/api';

const CategoryFilter = ({ selectedCategory, onCategoryChange }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(getApiUrl('categories'));
        if (response.ok) {
          const data = await response.json();
          setCategories(data.data.categories || []);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
      setLoading(false);
    };

    fetchCategories();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="spinner-small"></div>
      </div>
    );
  }

  const handleCategoryClick = (category) => {
    if (category === 'all') {
      onCategoryChange('all');
    } else {
      onCategoryChange(category._id);
    }
  };

  const renderDropdownLayout = () => (
    <div className="flex flex-wrap justify-center gap-3">
      {/* All Categories Button */}
      <button
        onClick={() => handleCategoryClick('all')}
        className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
          selectedCategory === 'all'
            ? 'bg-primary-500 text-white shadow-lg'
            : 'bg-white text-neutral-600 hover:bg-primary-50 hover:text-primary-600 border border-neutral-200'
        }`}
      >
        All Categories
      </button>

      {/* Tier 1 Categories with Dropdowns */}
      {categories.map((category) => (
        <div key={category._id} className="relative group">
          <button
            onClick={() => handleCategoryClick(category)}
            className={`px-6 py-3 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
              selectedCategory === category.name
                ? 'bg-primary-500 text-white shadow-lg'
                : 'bg-white text-neutral-600 hover:bg-primary-50 hover:text-primary-600 border border-neutral-200'
            } ${category.children?.length > 0 ? 'pr-8' : ''}`}
          >
            {category.name}
            {category.children?.length > 0 && (
              <FiChevronDown size={14} className="ml-1" />
            )}
          </button>

          {/* Dropdown for Tier 2 Categories */}
          {category.children?.length > 0 && (
            <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-neutral-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
              <div className="py-2">
                {category.children.map((subcategory) => (
                  <button
                    key={subcategory._id}
                    onClick={() => handleCategoryClick(subcategory)}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-neutral-50 transition-colors ${
                      selectedCategory === subcategory.name
                        ? 'bg-primary-50 text-primary-600 font-medium'
                        : 'text-neutral-600'
                    }`}
                  >
                    {subcategory.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderAccordionLayout = () => (
    <div className="max-w-4xl mx-auto">
      {/* All Categories Button */}
      <div className="mb-4">
        <button
          onClick={() => handleCategoryClick('all')}
          className={`w-full px-6 py-3 rounded-lg text-sm font-medium transition-all text-left ${
            selectedCategory === 'all'
              ? 'bg-primary-500 text-white shadow-lg'
              : 'bg-white text-neutral-600 hover:bg-primary-50 hover:text-primary-600 border border-neutral-200'
          }`}
        >
          All Categories
        </button>
      </div>

      {/* Tier 1 Categories */}
      {categories.map((category) => (
        <AccordionItem
          key={category._id}
          category={category}
          selectedCategory={selectedCategory}
          onCategoryClick={handleCategoryClick}
        />
      ))}
    </div>
  );

  const renderSidebarLayout = () => (
    <div className="w-64 bg-white border-r border-neutral-200 p-4">
      <h3 className="text-lg font-semibold text-neutral-800 mb-4">Categories</h3>

      {/* All Categories */}
      <button
        onClick={() => handleCategoryClick('all')}
        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all mb-2 ${
          selectedCategory === 'all'
            ? 'bg-primary-500 text-white'
            : 'text-neutral-600 hover:bg-primary-50 hover:text-primary-600'
        }`}
      >
        All Categories
      </button>

      {/* Tier 1 Categories */}
      {categories.map((category) => (
        <SidebarItem
          key={category._id}
          category={category}
          selectedCategory={selectedCategory}
          onCategoryClick={handleCategoryClick}
        />
      ))}
    </div>
  );

  const renderTabsLayout = () => (
    <div className="w-full">
      <div className="flex flex-wrap border-b border-neutral-200">
        {/* All Categories Tab */}
        <button
          onClick={() => handleCategoryClick('all')}
          className={`px-6 py-3 text-sm font-medium transition-all border-b-2 ${
            selectedCategory === 'all'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-neutral-600 hover:text-primary-600 hover:border-neutral-300'
          }`}
        >
          All Categories
        </button>

        {/* Tier 1 Category Tabs */}
        {categories.map((category) => (
          <div key={category._id} className="relative">
            <button
              onClick={() => handleCategoryClick(category)}
              className={`px-6 py-3 text-sm font-medium transition-all border-b-2 flex items-center gap-2 ${
                selectedCategory === category.name
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-neutral-600 hover:text-primary-600 hover:border-neutral-300'
              }`}
            >
              {category.name}
              {category.children?.length > 0 && <FiChevronDown size={14} />}
            </button>

            {/* Sub-tabs for Tier 2 Categories */}
            {category.children?.length > 0 && selectedCategory === category.name && (
              <div className="absolute top-full left-0 bg-white border border-neutral-200 rounded-lg shadow-lg mt-1 z-10 min-w-max">
                <div className="flex">
                  {category.children.map((subcategory) => (
                    <button
                      key={subcategory._id}
                      onClick={() => handleCategoryClick(subcategory)}
                      className={`px-4 py-2 text-sm border-r border-neutral-200 last:border-r-0 transition-colors ${
                        selectedCategory === subcategory.name
                          ? 'bg-primary-50 text-primary-600 font-medium'
                          : 'text-neutral-600 hover:bg-neutral-50'
                      }`}
                    >
                      {subcategory.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="mb-12">
      {renderDropdownLayout()}
    </div>
  );
};

// Accordion Item Component
const AccordionItem = ({ category, selectedCategory, onCategoryClick }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mb-2">
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          onCategoryClick(category);
        }}
        className={`w-full px-6 py-3 rounded-lg text-sm font-medium transition-all text-left flex items-center justify-between ${
          selectedCategory === category.name
            ? 'bg-primary-500 text-white shadow-lg'
            : 'bg-white text-neutral-600 hover:bg-primary-50 hover:text-primary-600 border border-neutral-200'
        }`}
      >
        {category.name}
        {category.children?.length > 0 && (
          <motion.div
            animate={{ rotate: isOpen ? 90 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <FiChevronRight size={16} />
          </motion.div>
        )}
      </button>

      <AnimatePresence>
        {isOpen && category.children?.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="ml-6 mt-2 space-y-1">
              {category.children.map((subcategory) => (
                <button
                  key={subcategory._id}
                  onClick={() => onCategoryClick(subcategory)}
                  className={`w-full px-4 py-2 rounded-lg text-sm font-medium transition-all text-left ${
                    selectedCategory === subcategory.name
                      ? 'bg-primary-100 text-primary-600 border border-primary-200'
                      : 'bg-neutral-50 text-neutral-600 hover:bg-primary-50 hover:text-primary-600 border border-neutral-200'
                  }`}
                >
                  {subcategory.name}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Sidebar Item Component
const SidebarItem = ({ category, selectedCategory, onCategoryClick }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mb-1">
      <button
        onClick={() => {
          if (category.children?.length > 0) {
            setIsOpen(!isOpen);
          }
          onCategoryClick(category);
        }}
        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-between ${
          selectedCategory === category.name
            ? 'bg-primary-500 text-white'
            : 'text-neutral-600 hover:bg-primary-50 hover:text-primary-600'
        }`}
      >
        <span>{category.name}</span>
        {category.children?.length > 0 && (
          <motion.div
            animate={{ rotate: isOpen ? 90 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <FiChevronRight size={14} />
          </motion.div>
        )}
      </button>

      <AnimatePresence>
        {isOpen && category.children?.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="ml-4 mt-1 space-y-1"
          >
            {category.children.map((subcategory) => (
              <button
                key={subcategory._id}
                onClick={() => onCategoryClick(subcategory)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                  selectedCategory === subcategory.name
                    ? 'bg-primary-100 text-primary-600 font-medium'
                    : 'text-neutral-500 hover:bg-neutral-50 hover:text-primary-600'
                }`}
              >
                {subcategory.name}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CategoryFilter;